import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, Pause, Volume2, Music4, Sparkles, Zap, ArrowLeft } from 'lucide-react';
import { FLAMINGO_TRACKS, CATEGORY_LABELS, DEFAULT_GAIN } from '../../games/luckyFlamingo/assets/audio/tracks';

const CAT_ORDER = ['base', 'bonus', 'sfx'];
const CAT_ICON = { base: Music4, bonus: Sparkles, sfx: Zap };

function TrackRow({ track, isPlaying, onToggle, volume, onVolume, progress }) {
  return (
    <div
      data-testid={`flamingo-audio-track-${track.id}`}
      className="flex flex-col gap-3 rounded-xl border border-pink-500/20 bg-black/40 p-4 transition-colors hover:border-pink-400/40"
    >
      <div className="flex items-center gap-4">
        <button
          data-testid={`flamingo-audio-play-${track.id}`}
          onClick={() => onToggle(track.id)}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-pink-500 text-white shadow-lg shadow-pink-500/30 transition-transform hover:scale-105 active:scale-95"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-semibold text-white">{track.title}</div>
          <div className="truncate text-xs text-pink-200/60">
            {track.pack} · {track.format} · {track.duration}
          </div>
        </div>
        <div className="hidden items-center gap-2 sm:flex">
          <Volume2 size={16} className="text-pink-300/70" />
          <input
            data-testid={`flamingo-audio-volume-${track.id}`}
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={(e) => onVolume(track.id, parseFloat(e.target.value))}
            className="h-1 w-28 cursor-pointer accent-pink-500"
          />
          <span className="w-9 text-right text-xs tabular-nums text-pink-200/60">
            {Math.round(volume * 100)}%
          </span>
        </div>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-gradient-to-r from-pink-400 to-fuchsia-500 transition-[width] duration-150"
          style={{ width: `${isPlaying ? progress : 0}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-2 text-[11px]">
        {['license', 'source', 'contentId'].map((k) => (
          <span
            key={k}
            className={`rounded-full px-2 py-0.5 ${
              track[k] === 'pending'
                ? 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30'
                : 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30'
            }`}
          >
            {k === 'contentId' ? 'Content-ID' : k[0].toUpperCase() + k.slice(1)}: {track[k]}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function FlamingoAudioAudition() {
  const [playingId, setPlayingId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [volumes, setVolumes] = useState(() =>
    Object.fromEntries(FLAMINGO_TRACKS.map((t) => [t.id, DEFAULT_GAIN[t.category] ?? 0.8]))
  );
  const audioRef = useRef(null);

  const grouped = useMemo(() => {
    const g = { base: [], bonus: [], sfx: [] };
    FLAMINGO_TRACKS.forEach((t) => (g[t.category] || (g[t.category] = [])).push(t));
    return g;
  }, []);

  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;
    const onTime = () =>
      setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
    const onEnd = () => {
      setPlayingId(null);
      setProgress(0);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnd);
    };
  }, []);

  const toggle = (id) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playingId === id) {
      audio.pause();
      setPlayingId(null);
      return;
    }
    const track = FLAMINGO_TRACKS.find((t) => t.id === id);
    if (!track) return;
    audio.src = track.src;
    audio.volume = volumes[id] ?? 0.8;
    audio.currentTime = 0;
    audio.play().catch(() => {});
    setPlayingId(id);
    setProgress(0);
  };

  const setVolume = (id, v) => {
    setVolumes((prev) => ({ ...prev, [id]: v }));
    if (playingId === id && audioRef.current) audioRef.current.volume = v;
  };

  return (
    <div className="min-h-screen bg-[#0a0510] px-4 py-8 text-white sm:px-8" data-testid="flamingo-audio-audition">
      <div className="mx-auto max-w-3xl">
        <Link
          to="/admin"
          data-testid="flamingo-audio-back"
          className="mb-6 inline-flex items-center gap-2 text-sm text-pink-300/70 transition-colors hover:text-pink-200"
        >
          <ArrowLeft size={16} /> Back to Admin
        </Link>

        <header className="mb-8">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-pink-500/15 px-3 py-1 text-xs font-medium uppercase tracking-wide text-pink-300 ring-1 ring-pink-500/30">
            Hidden · Admin Only
          </div>
          <h1 className="text-3xl font-bold sm:text-4xl">Lucky Flamingo — Audio Audition</h1>
          <p className="mt-2 max-w-xl text-sm text-pink-200/60">
            Preview every candidate track before it goes into the game. Music beds default
            25–35% lower than effects. Licenses marked <span className="text-amber-300">pending</span>{' '}
            must be filled in before anything ships publicly.
          </p>
        </header>

        <div className="space-y-8">
          {CAT_ORDER.map((cat) => {
            const tracks = grouped[cat] || [];
            const Icon = CAT_ICON[cat];
            return (
              <section key={cat} data-testid={`flamingo-audio-section-${cat}`}>
                <div className="mb-3 flex items-center gap-2">
                  <Icon size={18} className="text-pink-400" />
                  <h2 className="text-lg font-semibold text-pink-100">{CATEGORY_LABELS[cat]}</h2>
                  <span className="text-xs text-pink-200/40">({tracks.length})</span>
                </div>
                {tracks.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-pink-500/20 bg-black/20 p-6 text-center text-sm text-pink-200/40">
                    No tracks yet — upload files for this group.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tracks.map((t) => (
                      <TrackRow
                        key={t.id}
                        track={t}
                        isPlaying={playingId === t.id}
                        onToggle={toggle}
                        volume={volumes[t.id] ?? 0.8}
                        onVolume={setVolume}
                        progress={progress}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <footer className="mt-10 rounded-xl border border-pink-500/10 bg-black/30 p-4 text-xs text-pink-200/50">
          {FLAMINGO_TRACKS.length} track{FLAMINGO_TRACKS.length === 1 ? '' : 's'} loaded. Keep uploading
          packs (Velvet Lounge Vol. 2, Isla Adventure, Copacabana Pixel Vol. 1, Casino Slots SFX Vol. 1)
          and I'll add each here for review.
        </footer>
      </div>
    </div>
  );
}
